import React, { useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';

interface Props {
  content: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: number;
}

const Btn: React.FC<{
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
}> = ({ onClick, active, disabled, title, children }) => (
  <button
    type="button"
    onMouseDown={(e) => { e.preventDefault(); onClick(); }}
    disabled={disabled}
    title={title}
    className={`tiptap-toolbar-btn${active ? ' is-active' : ''}`}
  >
    {children}
  </button>
);

const RichTextEditor: React.FC<Props> = ({
  content,
  onChange,
  placeholder = 'Start typing…',
  minHeight = 200,
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link.configure({ openOnClick: false, HTMLAttributes: { rel: 'noopener noreferrer' } }),
      Placeholder.configure({ placeholder }),
    ],
    content,
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  const setLink = useCallback(() => {
    if (!editor) return;
    const prev = editor.getAttributes('link').href;
    const url = window.prompt('URL', prev);
    if (url === null) return;
    if (url === '') { editor.chain().focus().unsetLink().run(); return; }
    editor.chain().focus().setLink({ href: url }).run();
  }, [editor]);

  return (
    <div className="tiptap-wrapper">
      <div className="tiptap-toolbar">
        <div className="tiptap-toolbar-group">
          <Btn onClick={() => editor?.chain().focus().toggleBold().run()} active={editor?.isActive('bold')} title="Bold"><strong>B</strong></Btn>
          <Btn onClick={() => editor?.chain().focus().toggleItalic().run()} active={editor?.isActive('italic')} title="Italic"><em>I</em></Btn>
          <Btn onClick={() => editor?.chain().focus().toggleUnderline().run()} active={editor?.isActive('underline')} title="Underline"><span style={{ textDecoration: 'underline' }}>U</span></Btn>
          <Btn onClick={() => editor?.chain().focus().toggleStrike().run()} active={editor?.isActive('strike')} title="Strikethrough"><s>S</s></Btn>
        </div>
        <div className="tiptap-toolbar-divider" />
        <div className="tiptap-toolbar-group">
          <Btn onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()} active={editor?.isActive('heading', { level: 2 })} title="Heading 2">H2</Btn>
          <Btn onClick={() => editor?.chain().focus().toggleHeading({ level: 3 }).run()} active={editor?.isActive('heading', { level: 3 })} title="Heading 3">H3</Btn>
        </div>
        <div className="tiptap-toolbar-divider" />
        <div className="tiptap-toolbar-group">
          <Btn onClick={() => editor?.chain().focus().toggleBulletList().run()} active={editor?.isActive('bulletList')} title="Bullet list">&#8226;&#8212;</Btn>
          <Btn onClick={() => editor?.chain().focus().toggleOrderedList().run()} active={editor?.isActive('orderedList')} title="Numbered list">1&#8212;</Btn>
        </div>
        <div className="tiptap-toolbar-divider" />
        <div className="tiptap-toolbar-group">
          <Btn onClick={() => editor?.chain().focus().toggleBlockquote().run()} active={editor?.isActive('blockquote')} title="Blockquote">&#8220;&#8221;</Btn>
          <Btn onClick={() => editor?.chain().focus().toggleCode().run()} active={editor?.isActive('code')} title="Inline code">{"`"}</Btn>
          <Btn onClick={() => editor?.chain().focus().toggleCodeBlock().run()} active={editor?.isActive('codeBlock')} title="Code block">{"</>"}</Btn>
          <Btn onClick={setLink} active={editor?.isActive('link')} title="Link">&#128279;</Btn>
        </div>
        <div className="tiptap-toolbar-divider" />
        <div className="tiptap-toolbar-group">
          <Btn onClick={() => editor?.chain().focus().undo().run()} disabled={!editor?.can().undo()} title="Undo">&#8617;</Btn>
          <Btn onClick={() => editor?.chain().focus().redo().run()} disabled={!editor?.can().redo()} title="Redo">&#8618;</Btn>
        </div>
      </div>
      <EditorContent editor={editor} className="tiptap-editor" style={{ minHeight }} />
    </div>
  );
};

export default RichTextEditor;
